import { Box, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useMobile } from "lib/@components/mobile"
import { If } from "lib/@components/switch"
import { taskAssignmentTargetId } from "routes/skill-mapper/server/schedule-assignment-routing.mjs"
import { ScheduleTaskDetails } from "./schedule-task-details"
import { describeTaskRouting } from "./describe-task-routing"
import { RoutingNotes } from "./routing-notes"

const FOLLOW_SCHEDULE = "__follow__"

/**
 * One task in the schedule drawer with a trade picker: "Follow schedule" removes the
 * task's own assignment; any trade creates or moves it.
 */
export function ScheduleTaskRow({ task, index, schedule, followLabel }) {
    const { board, trades, readOnly, onAssignTargets, onRemoveAssignment } = useBoundContext()
    const targetId = taskAssignmentTargetId(schedule.scheduleId, task.taskId)
    const assignment = board.taskAssignments.get(targetId)
    const value = assignment?.tradeId ?? FOLLOW_SCHEDULE
    const tradeOptions = Object.entries(trades).sort(byName).map(toOption)
    const routing = describeTaskRouting({ task, scheduleId: schedule.scheduleId, board, trades })
    // Below the lg breakpoint the drawer is a dialog that closes on any mousedown outside its
    // content, and a portalled menu counts as outside: choosing a trade closed the drawer
    const isMobile = useMobile()

    const routeText = `${routing.tradeName ?? "No trade"} · ${routing.route}`
    const notes = [...routing.overrides, ...conditionalNote(routing.conditionalRules)]

    return (
        // Two lines, so a long schedule's tasks can be scanned: the title (wrapping, never cut)
        // beside the trade picker, then its details and why it goes where it does
        <Stack spacing={0.5} sx={{ pb: 1, borderBottom: 1, borderColor: "divider" }} data-cy="schedule-task-row">
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                <Typography variant="body2" sx={{ minWidth: 0, pt: 0.5 }}>
                    {index + 1}. {task.title}
                </Typography>
                <Select
                    size="small"
                    value={value}
                    onChange={handleChange}
                    // A stand-in has no server record yet, so it can't be changed until it saves
                    disabled={readOnly || !!assignment?.pending}
                    sx={{ width: 190, flexShrink: 0, fontSize: 13, "& .MuiSelect-select": { py: 0.5 } }}
                    inputProps={{ "aria-label": `Trade for ${task.title}` }}
                    MenuProps={{ disablePortal: isMobile }}
                    data-cy="schedule-task-trade"
                >
                    <MenuItem value={FOLLOW_SCHEDULE}>{followLabel}</MenuItem>
                    {tradeOptions}
                </Select>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                {/* Its own width, so the skill badge never runs into the route beside it */}
                <Box sx={{ flexShrink: 0 }}>
                    <ScheduleTaskDetails task={task} />
                </Box>
                {/* Why the task goes where it does, in the order the cost engine decides it;
                    what an assignment overrides and the rules it ignores are on the icon */}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    title={routeText}
                    sx={{ minWidth: 0 }}
                    data-cy="schedule-task-why"
                >
                    {routeText}
                </Typography>
                <If value={notes.length > 0}>
                    {/* A focusable span, as the tooltip needs an element that holds a ref (Iconify
                        doesn't) and keyboard users open it by tabbing to it */}
                    <Tooltip title={<RoutingNotes notes={notes} />}>
                        <Box
                            component="span"
                            tabIndex={0}
                            aria-label={notes.join(". ")}
                            sx={{ display: "inline-flex", color: "text.secondary", flexShrink: 0 }}
                            data-cy="schedule-task-overrides"
                        >
                            <Iconify icon="eva:info-outline" width={16} />
                        </Box>
                    </Tooltip>
                </If>
            </Stack>
        </Stack>
    )

    function handleChange(event) {
        const tradeId = event.target.value
        if (tradeId === FOLLOW_SCHEDULE) {
            if (assignment) onRemoveAssignment(assignment.id)
            return
        }
        onAssignTargets(tradeId, [{ kind: "task", targetId, name: `${schedule.code} · ${task.title}` }], {
            keepSelection: true,
        })
    }
}

// MenuItems must be direct Select children (MUI clones each one), so these are an
// array rather than a <Repeat>; see resolution-filter-options.js for the same reason.
function toOption([tradeId, tradeName]) {
    return (
        <MenuItem key={tradeId} value={tradeId}>
            {tradeName}
        </MenuItem>
    )
}

function byName([, a], [, b]) {
    return a.localeCompare(b)
}

// Skill-routed work names the conditional rules that could send it elsewhere
function conditionalNote(rules) {
    if (!rules.length) return []
    return [`Unless a conditional rule sends it elsewhere: ${rules.join(", ")}`]
}
